import React, { useContext, useEffect } from 'react';
import { Text, Divider } from '@oneaudi/unified-web-components';
import { useMediaQuery } from '@oneaudi/feature-app-utils';
import { LinkWithCurrentPage, SUBMENUID, TRIGGERID } from '../../../../ContentType';
import { Context } from '../../../../context/Context';
import {
  LinkList,
  LinkListItem,
  LinkListLink,
  MobileMenuButtonContainer,
  MobileMenuContainer,
  MobileMenuWrapper,
  UnderlinedText,
  MobileMenuButtonWrapper,
  MobileMenuButton,
} from '../../SubPageNavigationStyles';
import { useTrackingManager } from '../../../../utils/useTrackingManager';
import VtpButtons from '../../ui/VtpButtons';
import { attachVerticalNavigation } from '../../../../utils/a11y';
import { useI18n } from '../../../../i18n';

const MobileMenu: React.FC = () => {
  const {
    content: { cta, links, secondaryCta },
    hasCta,
    renderPrimaryButton,
    renderVtpButtons,
    renderSecondaryButton,
    submenuMobileRef,
    showSubmenu,
  } = useContext(Context);
  const secondaryButton = secondaryCta?.customCta;
  const { click } = useTrackingManager();
  const ariaSubmenu = useI18n('ariaSubmenu');
  const isMobile = useMediaQuery('(max-width: 767px)');

  const renderLink = (
    { text, href, currentPage, ariaLabel, opensInNewTab }: LinkWithCurrentPage,
    index: number,
  ) => {
    if (currentPage) {
      return (
        <LinkListItem key={text}>
          <UnderlinedText tag="span">
            <Text role="presentation" variant={{ type: 'copy', order: '2', style: 'normal' }}>
              {text}
            </Text>
          </UnderlinedText>
        </LinkListItem>
      );
    }
    return (
      <LinkListItem key={text}>
        <LinkListLink
          href={href}
          target={opensInNewTab ? '_blank' : '_self'}
          onClick={() => click('link', false, text, href, `${index + 1}`, '')}
          role="menuitem"
          aria-current={currentPage ? 'page' : undefined}
          aria-label={ariaLabel || undefined}
          variant="standalone"
          size={isMobile ? 'sm' : 'md'}
        >
          <Text tag="span" variant={{ type: 'copy', order: '2', style: 'normal' }}>
            {text}
          </Text>
        </LinkListLink>
      </LinkListItem>
    );
  };

  useEffect(() => {
    if (!submenuMobileRef.current) return undefined;
    const cleanup = attachVerticalNavigation(submenuMobileRef.current);

    return () => cleanup();
  }, [submenuMobileRef]);

  return (
    <MobileMenuContainer>
      <MobileMenuWrapper
        ref={submenuMobileRef}
        className={showSubmenu ? 'show' : undefined}
        id={SUBMENUID.MOBILE}
        aria-label={ariaSubmenu}
        aria-labelledby={TRIGGERID.MOBILE}
      >
        {/* Link list / subpages */}
        {links && links.length > 0 && (
          <LinkList>{links.map((link, index) => renderLink(link, index))}</LinkList>
        )}
        {/* Cta section */}
        {hasCta && (
          <MobileMenuButtonWrapper>
            <Divider className="divider" aria-hidden="true" />
            <MobileMenuButtonContainer>
              {renderPrimaryButton && (
                <MobileMenuButton
                  href={cta[0].href}
                  {...(cta[0].ariaLabel ? { 'aria-label': cta[0].ariaLabel } : undefined)}
                  target={cta[0].opensInNewTab ? '_blank' : '_self'}
                  variant="primary"
                  isFullWidth
                  size="sm"
                  onClick={() => click('cta', true, cta[0].text, cta[0].href, '', 'primary')}
                >
                  {cta[0].text}
                </MobileMenuButton>
              )}
              {renderVtpButtons && (
                <>
                  {renderPrimaryButton && <div style={{ paddingTop: '8px' }} />}
                  <VtpButtons />
                </>
              )}
              {renderSecondaryButton && (
                <MobileMenuButton
                  href={secondaryButton.href}
                  {...(cta[0].ariaLabel ? { 'aria-label': cta[0].ariaLabel } : undefined)}
                  variant="secondary"
                  isFullWidth
                  size="sm"
                  onClick={() =>
                    click('cta', true, secondaryButton.text, secondaryButton.href, '', 'secondary')
                  }
                >
                  {secondaryButton.text}
                </MobileMenuButton>
              )}
            </MobileMenuButtonContainer>
          </MobileMenuButtonWrapper>
        )}
      </MobileMenuWrapper>
    </MobileMenuContainer>
  );
};

export default MobileMenu;
